/**
 * Whether an equation can be drawn, and if not, why.
 *
 * The marker reads the LaTeX a student's answer holds; an equation KaTeX can't
 * draw is one the student didn't mean, so "Mark my answer" waits until it's
 * fixed rather than sending something garbled.
 *
 * KaTeX is loaded on first use, never at start-up: it's 300 KB that every
 * student would otherwise download before the first screen, and only theory
 * answers with equations need it. Until it's loaded, `latexProblem` reports
 * nothing; `loadLatexChecker()` loads it and resolves when checks are real.
 */

interface Katex {
  renderToString(tex: string, options?: { throwOnError?: boolean; strict?: string }): string;
}

let katex: Katex | null = null;
let loading: Promise<boolean> | null = null;

/** Load KaTeX and mhchem (for `\ce{}`). Resolves false if they can't be fetched. */
export function loadLatexChecker(): Promise<boolean> {
  if (katex) return Promise.resolve(true);
  loading ??= import("katex")
    .then(async (module) => {
      // Chemistry: `\ce{H2SO4}`, `\ce{Fe^3+}`, `\ce{A -> B}`. Registers with KaTeX.
      await import("katex/contrib/mhchem");
      katex = (module.default ?? module) as unknown as Katex;
      return true;
    })
    .catch(() => {
      loading = null;
      return false;
    });
  return loading;
}

/** True once equations can be checked. */
export const latexCheckerReady = () => katex !== null;

export function latexProblem(latex: string): string | null {
  if (!latex.trim()) return "An equation is empty.";
  // Not loaded yet: the screen loads it as soon as an answer holds an equation.
  if (!katex) return null;
  try {
    katex.renderToString(latex, { throwOnError: true, strict: "ignore" });
    return null;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // KaTeX prefixes "KaTeX parse error: "; the rest is the useful part.
    return message.replace(/^KaTeX parse error:\s*/, "");
  }
}

/** The first equation that can't be drawn, numbered from 0, or null. */
export function firstBadEquation(
  equations: string[],
): { index: number; latex: string; reason: string } | null {
  for (const [index, latex] of equations.entries()) {
    const reason = latexProblem(latex);
    if (reason) return { index, latex, reason };
  }
  return null;
}

/** Tests only: forget the loaded KaTeX, to check behaviour before it loads. */
export function resetLatexChecker(): void {
  katex = null;
  loading = null;
}
